import { useCallback, useEffect } from 'react';

import { useBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { FastshiftTicketStatus } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

import {
	useFastshiftDispatch,
	useFastshiftSelector,
	getFastshiftTopSupportRequests,
	FastshiftDispatchPromise,
} from '@/state';

type UseTopMigrationTickets = () => {
	pending: boolean;
	error: string | null;
	supportTickets: {
		supportTicketId: string;
		title: string;
		status: FastshiftTicketStatus;
	}[];
	total: number;
	handleRefresh: (customerDomainId: string) => FastshiftDispatchPromise<unknown> | undefined;
};

const TOP_SUPPORT_REQUESTS_COUNT = 3;

export const useTopMigrationTickets: UseTopMigrationTickets = () => {
	const dispatch = useFastshiftDispatch();
	const { data: customerDomainData } = useFastshiftSelector((state) => state.customerDomain);

	const stargateClient = useBxpWebStargateClient();

	const {
		pending,
		error,
		data: topSupportRequestsData,
		lastFetchParams,
	} = useFastshiftSelector((state) => state.topSupportRequests);

	const getTopSupportRequests = useCallback(
		(customerDomainId: string) => {
			return dispatch(
				getFastshiftTopSupportRequests({
					stargateClient,
					domainId: customerDomainId,
					count: TOP_SUPPORT_REQUESTS_COUNT,
					queryParams: {},
				}),
			);
		},
		[dispatch, stargateClient],
	);

	useEffect(() => {
		let topSupportRequestsPromise: FastshiftDispatchPromise<unknown> | null = null;

		if (customerDomainData) {
			// Check if we need to fetch based on lastFetchParams
			const needsFetch =
				!lastFetchParams ||
				lastFetchParams.customerDomainId !== customerDomainData.customerDomainId ||
				lastFetchParams.queryParams.orgId !== undefined ||
				lastFetchParams.queryParams.permissionLevel !== undefined;

			// Skip fetch if data already cached for these params
			if (!needsFetch) {
				return;
			}

			topSupportRequestsPromise =
				getTopSupportRequests(customerDomainData.customerDomainId) ?? null;
		}

		return () => {
			if (topSupportRequestsPromise) {
				topSupportRequestsPromise.abort();
			}
		};
	}, [dispatch, stargateClient, customerDomainData, lastFetchParams, getTopSupportRequests]);

	return {
		pending,
		error,
		...(topSupportRequestsData ? topSupportRequestsData : { supportTickets: [], total: 0 }),
		handleRefresh: getTopSupportRequests,
	};
};
